import { useState, useEffect } from 'react'

/**
 * OfflineBanner — shows a sticky top banner when the browser loses network connection.
 * Automatically hides (fade-out) when connectivity is restored.
 */
export default function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine)
  const [visible, setVisible] = useState(!navigator.onLine)

  useEffect(() => {
    const handleOffline = () => {
      setIsOffline(true)
      setVisible(true)
    }

    const handleOnline = () => {
      // Keep banner visible briefly with "back online" state, then fade out
      setIsOffline(false)
      const timer = setTimeout(() => setVisible(false), 2000)
      return () => clearTimeout(timer)
    }

    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)

    return () => {
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
    }
  }, [])

  if (!visible) return null

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        padding: '0.625rem 1rem',
        fontSize: '0.875rem',
        fontWeight: 500,
        transition: 'opacity 0.4s ease',
        opacity: isOffline ? 1 : 0,
        backgroundColor: isOffline ? '#fef08a' : '#bbf7d0',
        color: isOffline ? '#713f12' : '#14532d',
        borderBottom: isOffline ? '1px solid #fde047' : '1px solid #86efac',
      }}
    >
      {isOffline ? (
        <>
          <span aria-hidden="true">⚠️</span>
          <span>您目前已離線，部分功能可能無法使用</span>
        </>
      ) : (
        <>
          <span aria-hidden="true">✅</span>
          <span>網路已恢復連線</span>
        </>
      )}
    </div>
  )
}
